import {
    formatDateLong,
    formatDateTime,
    formatTime,
    getBrowserTimezone,
} from '../../lib/DateTime.js';
import TrashIcon from '../icons/TrashIcon.jsx';
import { useEffect, useMemo, useState } from 'react';
import TextInput from '../TextInput';
import BookableResourcesCheckboxes from '../BookableResourcesCheckboxes.jsx';
import BookableResourcesSelect from '../BookableResourcesSelect.jsx';
import DateTimeInput from '../DateTimeInput.jsx';
import ClassDetailsPanel from '../calendar/ClassDetailsPanel.jsx';
import AddClassSessionsPanel from './AddClassSessionsPanel.jsx';
import ShowTimezoneIfDifferent from '../ShowIfBrowserTimeZoneDifferent.jsx';
import ShowIfBrowserTimeZoneDifferent from '../ShowIfBrowserTimeZoneDifferent.jsx';
import { ChevronRightIcon } from '@heroicons/react/24/outline/index.js';
import PencilSquareIcon from '~/components/icons/PencilSquareIcon';
import Modal from '~/components/Modal';
import DateTime from '~/lib/NewDateTime';
import { useBusinessInfo } from '~/lib/Queries';
import useDeleteApi from '~/lib/useDeleteApi';
import * as Toast from '~/lib/Toast';
import useDelete from '~/lib/useDelete';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import Select from '~/components/Select';
import StartEndDateTime from '~/components/forms/StartEndDateTime';
import { addMinutes } from 'date-fns';
import InfoMessageText from '~/components/InfoMessageText';

function isChecked(checkedClasses, instance) {
    return checkedClasses && checkedClasses.indexOf(instance.id) > -1;
}

function setChecked(checkedClasses, setCheckedClasses, instance) {
    if (isChecked(checkedClasses, instance)) {
        // Remove
        setCheckedClasses(old => {
            let newChecked = [...old];
            newChecked.splice(newChecked.indexOf(instance.id), 1);
            return newChecked;
        });
    } else {
        // Add it
        setCheckedClasses(old => [...old, instance.id]);
    }
}

function canBeChecked(instance) {
    return instance.id && !instance.attendees_count;
}

const ClassRow = ({
    instance,
    onDelete,
    isFirst,
    onClick,
    isChecked,
    setChecked,
    type,
}) => {
    // Show the date time to the user in the time it's offered on the bookable resource
    //  (which can be different than their browser time zone)
    const { data: businessData } = useBusinessInfo();
    const dateTime = DateTime.fromJsDate(
        new Date(instance.start),
    ).updateTimezone(
        instance.bookable_resource?.timezone ||
            businessData?.business?.timezone,
    );

    return (
        <div className="flex">
            <label className="w-5 h-5 mr-4 pt-4">
                {canBeChecked(instance) && (
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={setChecked}
                        value={instance.id}
                    />
                )}
            </label>

            <div
                className={`flex items-center flex-1 justify-between pr-6 py-4 ${!isFirst ? 'border-t' : ''}`}
            >
                <div
                    className={`group ${!!instance.id ? 'cursor-pointer' : ''} flex-1`}
                    onClick={!!instance.id ? onClick : null}
                >
                    <div>
                        <StartEndDateTime
                            start={dateTime.toJsDate()}
                            end={dateTime
                                .add((type.duration && type.duration % 1440 === 0) ? type.duration - 1 : (type.duration || 0), 'minute')
                                .toJsDate()}
                        />
                        {/*{dateTime.formatDateLong()} {dateTime.formatTime()}*/}
                    </div>
                    <div className="text-gray-500 text-sm">
                        {!instance.bookable_resource && (
                            <span className="text-red-600">
                                The person this was assigned to has been deleted
                            </span>
                        )}
                        {!!instance.bookable_resource?.name && (
                            <> with {instance.bookable_resource?.name}</>
                        )}
                        {!!instance.max_attendees && (
                            <>
                                {' '}
                                ({instance.attendees_count || 0}/
                                {instance.max_attendees} attendees)
                            </>
                        )}
                    </div>
                    {!!instance.max_attendees &&
                        parseInt(instance.max_attendees) !==
                            parseInt(type.settings?.max_attendees) && (
                            <InfoMessageText showIcon={true} className="mt-2">
                                Max attendees ({instance.max_attendees}) is
                                different from default settings (
                                {type.settings?.max_attendees})
                            </InfoMessageText>
                        )}
                </div>
                {!!instance.id && (
                    <div onClick={onClick}>
                        <ChevronRightIcon className="w-6 h-6 cursor-pointer text-gray-400 group-hover:text-gray-500" />
                    </div>
                )}
                {!instance.id /*Don't allow deleting from here if it's saved*/ && (
                    <button
                        className="btn-pink-pill"
                        type="button"
                        onClick={onDelete}
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
};

const ClassGroup = ({
    group,
    groupIdx,
    groupLabel,
    isSeries,
    className,
    setSelectedClass,
    onDelete,
    onClickAdd,
    onUpdateLabel,
    checkedClasses,
    setCheckedClasses,
    type,
}) => {
    const [isEditingLabel, setIsEditingLabel] = useState(false);

    const hasAttendees = Math.max(...group.map(i => i.attendees_count)) > 0;
    const sessionLabel = `Session ${groupIdx + 1}`;
    const [label, setLabel] = useState(groupLabel);

    return (
        <div className={`ml-4 ${isSeries ? className : ''}`}>
            {isSeries && (
                <div>
                    <div className="input-label flex items-center">
                        {label || sessionLabel}
                        <button
                            type="button"
                            className="text-primary-700 ml-2"
                            onClick={() => setIsEditingLabel(true)}
                        >
                            <PencilSquareIcon className="w-4 h-4" />
                        </button>
                    </div>
                    <Modal
                        isOpen={isEditingLabel}
                        onClose={() => {
                            setIsEditingLabel(false);
                            setLabel(groupLabel);
                        }}
                    >
                        <TextInput
                            value={label}
                            onChange={setLabel}
                            autoFocus={true}
                            label="Description"
                        />
                        <button
                            type="button"
                            className="btn-save mt-4"
                            onClick={() => {
                                onUpdateLabel(label);
                                setIsEditingLabel(false);
                            }}
                        >
                            Save
                        </button>
                    </Modal>
                </div>
            )}
            <div className={isSeries ? `border rounded-lg pl-4 mr-6` : ''}>
                {group.map((instance, idx) => (
                    <ClassRow
                        key={idx}
                        instance={instance}
                        type={type}
                        isFirst={idx + (isSeries ? 0 : groupIdx) === 0}
                        onDelete={() => onDelete(instance)}
                        onClick={() => setSelectedClass(instance)}
                        isChecked={isChecked(checkedClasses, instance)}
                        setChecked={() =>
                            setChecked(
                                checkedClasses,
                                setCheckedClasses,
                                instance,
                            )
                        }
                    />
                ))}
            </div>
            {isSeries && !hasAttendees && (
                <button
                    type="button"
                    className="btn-inverse-pill-sm mt-2"
                    onClick={onClickAdd}
                >
                    Add Class to {label || sessionLabel}
                </button>
            )}
        </div>
    );
};

const ClassInstances = ({
    instances,
    isSeries,
    setInstances,
    className,
    onClassUpdated,
    groupLabels,
    setGroupLabels,
    typeId,
    type,
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [addGroupKey, setAddGroupKey] = useState(null);
    const [showPast, setShowPast] = useState(false);

    const [checkedClasses, setCheckedClasses] = useState([]);

    const [selectedClass, setSelectedClass] = useState(null);

    const { save: saveDelete, isSaving: isDeleting } = useBusinessPostApi(
        `/event-types/${typeId}/classes/bulk-delete`,
    );

    const [filterOption, setFilterOption] = useState('all');
    const filterOptions = useMemo(() => {
        return {
            all: 'Show all',
            empty: 'Classes with no attendees',
            open: 'Classes with spots free',
            full: 'Full classes',
        };
    }, []);

    const allInstances = instances;
    instances = (instances || []).filter(i => {
        if (filterOption === 'all') {
            return true;
        } else if (filterOption === 'empty') {
            return i.attendees_count === 0;
        } else if (filterOption === 'open') {
            return i.attendees_count < i.max_attendees;
        } else if (filterOption === 'full') {
            return i.attendees_count >= i.max_attendees;
        }
    });
    let incrementingKey = 1;
    const instanceGroups = Object.values(
        Object.groupBy(instances, i => i.group_key || incrementingKey++),
    );

    function isGroupPast(g) {
        return new Date(g[g.length - 1].start) < now && !!g[g.length - 1].id;
    }

    const now = new Date();
    const pastGroups = instanceGroups.filter(g => isGroupPast(g));

    function doDeleteSelected() {
        if (!confirm('Are you sure you want to delete the selected classes?')) {
            return;
        }

        saveDelete({
            instances: checkedClasses,
        }).then(result => {
            onClassUpdated();
            setCheckedClasses([]);
            Toast.success(
                `Successfully deleted ${result.length} class${result.length > 1 ? 'es' : ''}`,
            );
        });
    }

    function getAllCheckboxes() {
        return document.querySelectorAll(
            '.class-instance-container input[type="checkbox"]',
        );
    }

    function setAllChecked(newChecked) {
        if (!newChecked) {
            setCheckedClasses([]);
            return;
        }

        let checked = [];
        for (let checkbox of getAllCheckboxes()) {
            checked.push(parseInt(checkbox.value));
        }

        setCheckedClasses(checked);
    }

    const [isAllChecked, setIsAllChecked] = useState(false);
    useEffect(() => {
        for (let checkbox of getAllCheckboxes()) {
            if (!checkbox.checked) {
                setIsAllChecked(false);
                return;
            }
        }

        setIsAllChecked(true);
    });

    return (
        <div>
            {allInstances?.length > 0 && (
                <div className="flex items-center justify-between max-w-2xl mb-2">
                    <Select
                        options={filterOptions}
                        onChange={newValue => {
                            setFilterOption(newValue);
                            setCheckedClasses([]);
                        }}
                        value={filterOption}
                    />
                    {pastGroups.length > 0 && (
                        <button
                            type="button"
                            className="btn-light-pill mb-2"
                            onClick={() => setShowPast(!showPast)}
                        >
                            {showPast ? 'Hide' : 'Show'} {pastGroups.length}{' '}
                            past{' '}
                            {isSeries ? `session${pastGroups.length > 1 ? 's' : ''}` : `class${pastGroups.length > 1 ? 'es' : ''}`}

                        </button>
                    )}
                </div>
            )}

            <div className="px-4 max-w-2xl bg-gray-150 rounded-t-lg sticky top-0 flex items-center justify-between">
                {instances.length > 0 && !!typeId && (
                    <>
                        <div className="flex items-center space-x-4 py-3">
                            <input
                                type="checkbox"
                                checked={isAllChecked}
                                onChange={() => setAllChecked(!isAllChecked)}
                            />
                            <div>Select all</div>
                        </div>
                        {checkedClasses?.length > 0 && (
                            <button
                                type="button"
                                className="btn-pink-pill"
                                onClick={doDeleteSelected}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}{' '}
                                {checkedClasses?.length} Class
                                {checkedClasses?.length > 1 ? 'es' : ''}
                            </button>
                        )}
                    </>
                )}
            </div>
            <div
                className={`${instances.length > 0 ? '!rounded-t-none' : ''} class-instance-container panel ${className}`}
            >
                {pastGroups.length > 0 && (
                    <div>
                        {showPast && (
                            <>
                                {pastGroups.map((group, idx) => (
                                    <ClassGroup
                                        isSeries={isSeries}
                                        key={idx}
                                        type={type}
                                        className={idx > 0 ? 'mt-4' : 'pt-4'}
                                        group={group}
                                        groupIdx={idx}
                                        groupLabel={
                                            group[0]?.settings?.label || ''
                                        }
                                        setSelectedClass={setSelectedClass}
                                        onClickAdd={() => {
                                            setAddGroupKey(group[0].group_key);
                                            setIsAdding(true);
                                        }}
                                        onUpdateLabel={() => null}
                                        checkedClasses={checkedClasses}
                                        setCheckedClasses={setCheckedClasses}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                )}
                {instanceGroups.map((group, idx) => (
                    <div key={idx}>
                        {!isGroupPast(group) && (
                            <ClassGroup
                                isSeries={isSeries}
                                group={group}
                                groupIdx={idx - pastGroups.length}
                                className={'pt-4'}
                                type={type}
                                setSelectedClass={setSelectedClass}
                                onClickAdd={() => {
                                    setAddGroupKey(group[0].group_key);
                                    setIsAdding(true);
                                }}
                                onDelete={instance => {
                                    const newInstances = [...instances];
                                    const instanceIdx = newInstances.findIndex(
                                        i => {
                                            return (
                                                i.start === instance.start &&
                                                i.group_key ===
                                                    instance.group_key
                                            );
                                        },
                                    );
                                    newInstances.splice(instanceIdx, 1);
                                    setInstances(newInstances);
                                }}
                                groupLabel={group[0]?.settings?.label || ''}
                                onUpdateLabel={newLabel => {
                                    // This is a bit messy that we're getting the group label
                                    //  from the first instance but then updating
                                    //  state just for the new one
                                    setGroupLabels({
                                        ...groupLabels,
                                        [group[0].group_key]: newLabel,
                                    });
                                }}
                                checkedClasses={checkedClasses}
                                setCheckedClasses={setCheckedClasses}
                            />
                        )}
                    </div>
                ))}

                <ClassDetailsPanel
                    isOpen={!!selectedClass}
                    onClose={() => setSelectedClass(null)}
                    selectedClass={selectedClass}
                    onClassUpdated={onClassUpdated}
                />

                {!allInstances?.length && (
                    <div className="pt-4 text-center mx-8 text-base text-gray-500">
                        Add times that this{' '}
                        {isSeries ? 'class series' : 'class'} is offered at.
                        {isSeries
                            ? ' Because this is a class series clients will be be registered for all the class times within each session.'
                            : ''}
                    </div>
                )}

                <div className="flex justify-center pt-6 pb-6">
                    <button
                        onClick={() => {
                            setIsAdding(true);
                            setAddGroupKey(null);
                        }}
                        className="btn-light-pill"
                        type="button"
                    >
                        {isSeries ? 'Add New Session' : 'Add Class'}
                    </button>
                </div>

                <AddClassSessionsPanel
                    isSeries={isSeries}
                    isOpen={isAdding}
                    onClose={() => setIsAdding(false)}
                    instances={instances}
                    setInstances={setInstances}
                    addToGroupKey={addGroupKey}
                    defaultMaxAttendees={type?.settings?.max_attendees}
                />
            </div>
        </div>
    );
};

export default ClassInstances;
